import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Button from '../../BaseComponents/Button';
import Loader from '../../BaseComponents/Loader';
import Modal from '../../BaseComponents/Modal';
import TableHeader from '../../BaseComponents/TableHeader';
import TableRowActionMenu from '../../BaseComponents/TableRowActionMenu';
import TextArea from '../../BaseComponents/TextArea';
import TextInput from '../../BaseComponents/TextInput';
import {
  CreateNamedEntityRequest,
  NamedEntity,
  UpdateNamedEntityRequest,
} from '../../../api/mriRadiology';
import { Formik } from 'formik';

type NamedEntityCrudSectionProps = {
  entityLabel: string;
  entityLabelPlural: string;
  addButtonText?: string;
  readOnly?: boolean;
  fetchList: (limit?: number, offset?: number, name?: string) => Promise<{ items: NamedEntity[] }>;
  createEntity?: (payload: CreateNamedEntityRequest) => Promise<unknown>;
  updateEntity?: (payload: UpdateNamedEntityRequest) => Promise<unknown>;
  deleteEntity?: (id: number) => Promise<unknown>;
};

const headerColumns = [
  { title: 'Name', key: 'name', minWidth: '220px', flex: 0 },
  { title: 'Description', key: 'description', minWidth: '280px', flex: 1 },
  { title: '', key: 'actions', minWidth: '50px', flex: 0 },
];

const readOnlyHeaderColumns = [
  { title: 'Name', key: 'name', minWidth: '220px', flex: 0 },
  { title: 'Description', key: 'description', minWidth: '280px', flex: 1 },
];

const NamedEntityCrudSection: React.FC<NamedEntityCrudSectionProps> = ({
  entityLabel,
  entityLabelPlural,
  addButtonText,
  readOnly = false,
  fetchList,
  createEntity,
  updateEntity,
  deleteEntity,
}) => {
  const [items, setItems] = useState<NamedEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [entityToEdit, setEntityToEdit] = useState<NamedEntity | null>(null);
  const [entityToDelete, setEntityToDelete] = useState<NamedEntity | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadItems = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchList();
      setItems(response.items ?? []);
    } catch (error) {
      console.error(error);
      toast.error(`Failed to load ${entityLabelPlural.toLowerCase()}`);
    } finally {
      setLoading(false);
    }
  }, [entityLabelPlural, fetchList]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const handleAdd = () => {
    setEntityToEdit(null);
    setIsFormOpen(true);
  };

  const handleEdit = (entity: NamedEntity) => {
    setEntityToEdit(entity);
    setIsFormOpen(true);
  };

  const handleDeleteClick = (entity: NamedEntity) => {
    setEntityToDelete(entity);
    setIsDeleteOpen(true);
  };

  const handleDelete = async () => {
    if (!entityToDelete || !deleteEntity) return;
    try {
      setIsDeleting(true);
      await deleteEntity(entityToDelete.id);
      toast.success(`${entityLabel} deleted successfully`);
      setIsDeleteOpen(false);
      setEntityToDelete(null);
      await loadItems();
    } catch (error) {
      console.error(error);
      toast.error(`Failed to delete ${entityLabel.toLowerCase()}`);
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div className='named-entity-crud-section'>
      <div className='search-row-container'>
        <div className='search-and-filter-container'>
          <div className='count-container'>
            {items.length} {items.length === 1 ? entityLabel : entityLabelPlural}
          </div>
        </div>
        {!readOnly && addButtonText && (
          <Button isSecondary text={addButtonText} onClick={handleAdd} />
        )}
      </div>

      <div className='scroll-container'>
        <div className='table-container named-entity-table'>
          <TableHeader
            headerColumns={readOnly ? readOnlyHeaderColumns : headerColumns}
            gap='24px'
          />
          <div className='table-body'>
            {items.length > 0 ? (
              items.map((item) => (
                <div key={item.id} className='table-row named-entity-table-row'>
                  <div className='entity-name'>{item.name}</div>
                  <div className='entity-description'>{item.description || '-'}</div>
                  {!readOnly && (
                    <div className='actions'>
                      <TableRowActionMenu
                        options={[
                          { key: 'edit', value: 'Edit' },
                          { key: 'delete', value: 'Delete' },
                        ]}
                        id={item.id}
                        onActionMenuClick={(_id, action) => {
                          if (action === 'edit') handleEdit(item);
                          if (action === 'delete') handleDeleteClick(item);
                        }}
                      />
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className='empty-state'>No {entityLabelPlural.toLowerCase()} found.</div>
            )}
          </div>
        </div>
      </div>

      {!readOnly && (
        <>
      <Modal
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEntityToEdit(null);
        }}
        title={entityToEdit ? `Edit ${entityLabel}` : `Add ${entityLabel}`}
        width='800px'
      >
        <Formik
          enableReinitialize
          initialValues={{
            name: entityToEdit?.name ?? '',
            description: entityToEdit?.description ?? '',
          }}
          onSubmit={async (values) => {
            if (!createEntity || !updateEntity) return;
            try {
              setIsSaving(true);
              const payload = {
                name: values.name.trim(),
                description: values.description.trim() || null,
              };
              if (entityToEdit) {
                await updateEntity({ id: entityToEdit.id, ...payload });
                toast.success(`${entityLabel} updated successfully`);
              } else {
                await createEntity(payload);
                toast.success(`${entityLabel} added successfully`);
              }
              setIsFormOpen(false);
              setEntityToEdit(null);
              await loadItems();
            } catch (error) {
              console.error(error);
              toast.error(`Failed to save ${entityLabel.toLowerCase()}`);
            } finally {
              setIsSaving(false);
            }
          }}
        >
          {({ handleSubmit, values, setFieldValue }) => (
            <form onSubmit={handleSubmit} className='named-entity-form'>
              <TextInput
                name='name'
                title='Name *'
                placeholder='Enter name'
                value={values.name}
                onChange={(e) => setFieldValue('name', e.target.value)}
                isError={false}
              />
              <TextArea
                name='description'
                title='Description'
                placeholder='Enter description'
                value={values.description}
                onChange={(e) => setFieldValue('description', e.target.value)}
                isError={false}
              />
              <div className='btn-wrapper'>
                <Button
                  text={entityToEdit ? 'Save changes' : addButtonText}
                  type='submit'
                  isDisabled={!values.name.trim()}
                  isLoading={isSaving}
                />
              </div>
            </form>
          )}
        </Formik>
      </Modal>

      <Modal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} width='420px'>
        <div className='delete-block-container'>
          <div className='modal-header'>
            <div className='title'>Are you sure you want to delete this {entityLabel.toLowerCase()}?</div>
          </div>
          <div className='submit-btn-wrapper'>
            <Button text='No' onClick={() => setIsDeleteOpen(false)} />
            <Button text='Yes' onClick={handleDelete} isLoading={isDeleting} />
          </div>
        </div>
      </Modal>
        </>
      )}
    </div>
  );
};

export default NamedEntityCrudSection;
